import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import SEO from '../../components/SEO';
import { 
  Search, 
  Building2,
  ArrowLeft,
  Clock,
  Calendar,
  ExternalLink,
  CheckCircle,
  Globe,
  Sparkles
} from 'lucide-react';

const SOURCES = [
  {
    name: 'BOAMP',
    fullName: 'Bulletin Officiel des Annonces des Marchés Publics',
    url: 'https://www.boamp.fr',
    type: 'Officiel',
    description: 'La source officielle de l\'État français. Tous les marchés publics nationaux y sont publiés.',
    pros: ['Exhaustif pour les marchés d\'État', 'Recherche avancée', 'Alertes email'],
    coverage: 'État, collectivités, établissements publics'
  },
  {
    name: 'DECP',
    fullName: 'Données Essentielles de la Commande Publique',
    url: 'https://data.gouv.fr',
    type: 'Open Data',
    description: 'Base de données open data regroupant tous les marchés attribués en France.',
    pros: ['Données structurées', 'API disponible', 'Historique complet'],
    coverage: 'Tous les acheteurs publics'
  },
  {
    name: 'TED Europa',
    fullName: 'Tenders Electronic Daily',
    url: 'https://ted.europa.eu',
    type: 'Européen',
    description: 'Le portail européen pour tous les marchés dépassant les seuils communautaires.',
    pros: ['Marchés européens', 'Gros montants', 'Multi-langues'],
    coverage: 'Union Européenne (27 pays)'
  },
  {
    name: 'PLACE',
    fullName: 'Plateforme des Achats de l\'État',
    url: 'https://www.marches-publics.gouv.fr',
    type: 'Officiel',
    description: 'Plateforme de dématérialisation des marchés de l\'État et de ses établissements.',
    pros: ['Interface moderne', 'Dépôt en ligne', 'Suivi des offres'],
    coverage: 'Ministères, services de l\'État'
  },
  {
    name: 'Marchés Sécurisés',
    fullName: 'AWS - Achat Web Services',
    url: 'https://www.marches-securises.fr',
    type: 'Privé',
    description: 'Une des principales plateformes privées de dématérialisation.',
    pros: ['Large couverture', 'Utilisé par nombreuses collectivités'],
    coverage: 'Collectivités territoriales'
  },
  {
    name: 'Maximilien',
    fullName: 'Maximilien Île-de-France',
    url: 'https://marches.maximilien.fr',
    type: 'Régional',
    description: 'Portail dédié aux marchés de la région Île-de-France.',
    pros: ['Spécialisé IDF', 'Grands donneurs d\'ordres'],
    coverage: 'Île-de-France uniquement'
  },
  {
    name: 'E-marchespublics',
    fullName: 'E-marchespublics.com',
    url: 'https://www.e-marchespublics.com',
    type: 'Privé',
    description: 'Plateforme utilisée par de nombreuses collectivités et établissements.',
    pros: ['Interface intuitive', 'Alertes personnalisées'],
    coverage: 'Collectivités, hôpitaux, offices HLM'
  },
  {
    name: 'SNCF Réseau',
    fullName: 'Portail Achats SNCF',
    url: 'https://www.sncf.com/fr/groupe/fournisseurs',
    type: 'Entreprise',
    description: 'Les marchés du groupe SNCF, un des plus gros acheteurs publics français.',
    pros: ['Gros volumes', 'Marchés récurrents', 'BTP, IT, services'],
    coverage: 'Groupe SNCF'
  },
  {
    name: 'ADP',
    fullName: 'Aéroports de Paris',
    url: 'https://www.parisaeroport.fr/groupe/fournisseurs',
    type: 'Entreprise',
    description: 'Les marchés des aéroports parisiens (CDG, Orly, Le Bourget).',
    pros: ['Projets d\'envergure', 'Construction, services'],
    coverage: 'Aéroports parisiens'
  },
  {
    name: 'RATP',
    fullName: 'Portail Fournisseurs RATP',
    url: 'https://www.ratp.fr/groupe-ratp/fournisseurs',
    type: 'Entreprise',
    description: 'Les marchés de la RATP pour le réseau de transport francilien.',
    pros: ['Transport, maintenance', 'IT, services'],
    coverage: 'RATP et filiales'
  }
];

export default function SourcesMarchesArticle() {
  return (
    <>
      <SEO 
        title="Sources des Marchés Publics : BOAMP, TED, DECP"
        description="Découvrez toutes les sources officielles pour trouver des appels d'offres : BOAMP, TED Europa, DECP, plateformes régionales."
        path="/blog/sources-marches-publics"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="container mx-auto px-4 py-3">
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article>
        <header className="bg-gradient-to-b from-blue-50 to-white py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                Ressources
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Calendar className="w-4 h-4" />
                28 janvier 2026
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Clock className="w-4 h-4" />
                8 min de lecture
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Les 10 Meilleures Sources pour Trouver des Marchés Publics en 2026
            </h1>
            
            <p className="text-xl text-slate-600">
              BOAMP, DECP, TED Europa, plateformes privées... Découvrez toutes les sources 
              pour ne manquer aucun appel d'offres et maximiser vos opportunités.
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="container mx-auto px-4 max-w-4xl py-12">
          
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-lg text-slate-700 mb-6">
              Trouver des <strong>appels d'offres publics</strong> peut être un véritable casse-tête. 
              Les marchés sont dispersés sur de nombreuses plateformes, chacune avec ses spécificités. 
              Voici notre guide complet des <strong>10 meilleures sources</strong> à surveiller en 2026.
            </p>

            <Card className="bg-violet-50 border-violet-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-violet-900 mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Astuce Mon DCE Express
                </h3>
                <p className="text-violet-800">
                  Plutôt que de surveiller manuellement toutes ces sources, notre plateforme les agrège 
                  automatiquement et utilise l'IA pour vous recommander les marchés les plus pertinents. 
                  <Link to="/" className="underline font-medium ml-1">Essayez gratuitement →</Link>
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Sources List */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Les 10 sources incontournables</h2>
            
            <div className="space-y-6">
              {SOURCES.map((source, index) => (
                <Card key={source.name} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-16 bg-slate-100 flex items-center justify-center p-4 md:p-0">
                        <span className="text-2xl font-bold text-slate-400">{index + 1}</span>
                      </div>
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-bold text-slate-900">{source.name}</h3>
                            <p className="text-sm text-slate-500">{source.fullName}</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            source.type === 'Officiel' ? 'bg-green-100 text-green-700' :
                            source.type === 'Européen' ? 'bg-blue-100 text-blue-700' :
                            source.type === 'Open Data' ? 'bg-purple-100 text-purple-700' :
                            source.type === 'Régional' ? 'bg-amber-100 text-amber-700' :
                            source.type === 'Entreprise' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {source.type}
                          </span>
                        </div>
                        
                        <p className="text-slate-600 mb-4">{source.description}</p>
                        
                        <div className="flex flex-wrap gap-4 mb-4">
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Avantages</p>
                            <div className="flex flex-wrap gap-1">
                              {source.pros.map((pro, i) => (
                                <span key={`item-${i}`} className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                                  <CheckCircle className="w-3 h-3" />
                                  {pro}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">
                            <Globe className="w-4 h-4 inline mr-1" />
                            {source.coverage}
                          </span>
                          <a 
                            href={source.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-violet-600 hover:text-violet-700 text-sm font-medium"
                          >
                            Visiter le site
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Comparison Table */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Tableau comparatif</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-4 font-semibold">Source</th>
                    <th className="text-left p-4 font-semibold">Type</th>
                    <th className="text-left p-4 font-semibold">Couverture</th>
                    <th className="text-left p-4 font-semibold">Gratuit</th>
                  </tr>
                </thead>
                <tbody>
                  {SOURCES.slice(0, 6).map((source, i) => (
                    <tr key={source.name} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-4 font-medium">{source.name}</td>
                      <td className="p-4">{source.type}</td>
                      <td className="p-4 text-sm text-slate-600">{source.coverage}</td>
                      <td className="p-4">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Conclusion</h2>
            <p className="text-slate-600 mb-4">
              La veille des marchés publics nécessite de surveiller de nombreuses sources différentes. 
              Chaque plateforme a ses spécificités et son périmètre de couverture. Pour être efficace, 
              vous devez soit y consacrer beaucoup de temps, soit utiliser un outil qui centralise ces sources.
            </p>
            <p className="text-slate-600">
              <strong>Mon DCE Express</strong> agrège automatiquement plus de 20 sources officielles et 
              utilise l'intelligence artificielle pour vous recommander uniquement les marchés pertinents 
              pour votre entreprise. Gagnez des heures de veille chaque semaine !
            </p>
          </section>

        </div>

        {/* CTA */}
        <section className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Centralisez votre veille en un clic
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Plus besoin de surveiller 10 sites différents. Notre IA fait le travail pour vous.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100">
                <Sparkles className="w-5 h-5 mr-2" />
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </section>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center text-sm">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
